// src/lib/dataEvents.js
//
// Tiny, dependency-free event bus for cross-page data invalidation.
//
// Why this exists:
//   When the user changes data anywhere (saving a transaction, deleting a
//   bill, updating a budget category), other pages with stale views should
//   know to refetch. Threading "refreshKey" props through Outlet context
//   only works for top-down signals from AppShell. This bus works in any
//   direction — pages, modals, sheets, and components can all emit events
//   that any other listener can react to.
//
// Usage:
//   import { useDataEvent, emitDataEvent, EVENTS } from '../lib/dataEvents'
//
//   // To trigger a refetch elsewhere:
//   emitDataEvent(EVENTS.TRANSACTION_SAVED)
//
//   // To listen and refetch when something changes:
//   useDataEvent(EVENTS.TRANSACTION_SAVED, () => fetchAll())
//
import { useEffect, useRef } from 'react'

// Canonical event names. Always use these constants — no string typos.
export const EVENTS = {
  // Transactions
  TRANSACTION_SAVED:   'transaction:saved',
  TRANSACTION_UPDATED: 'transaction:updated',
  TRANSACTION_DELETED: 'transaction:deleted',

  // Budgets
  BUDGET_SAVED:        'budget:saved',
  BUDGET_DELETED:      'budget:deleted',

  // Budget categories
  CATEGORY_SAVED:      'category:saved',
  CATEGORY_UPDATED:    'category:updated',
  CATEGORY_DELETED:    'category:deleted',

  // Bills
  BILL_SAVED:          'bill:saved',
  BILL_UPDATED:        'bill:updated',
  BILL_DELETED:        'bill:deleted',

  // Income
  INCOME_SAVED:        'income:saved',
  INCOME_UPDATED:      'income:updated',
  INCOME_DELETED:      'income:deleted',

  // Debts
  DEBT_SAVED:          'debt:saved',
  DEBT_UPDATED:        'debt:updated',
  DEBT_DELETED:        'debt:deleted',

  // Goals
  GOAL_SAVED:          'goal:saved',
  GOAL_UPDATED:        'goal:updated',
  GOAL_DELETED:        'goal:deleted',

  // Accounts (Net Worth)
  ACCOUNT_SAVED:       'account:saved',
  ACCOUNT_UPDATED:     'account:updated',
  ACCOUNT_DELETED:     'account:deleted',
  ACCOUNT_BALANCE_UPDATED: 'account:balance:updated',

  // Payment methods
  PAYMENT_METHOD_SAVED:   'payment_method:saved',
  PAYMENT_METHOD_DELETED: 'payment_method:deleted',

  // Profile / partner / currency
  PROFILE_UPDATED:     'profile:updated',
  PARTNER_LINKED:      'partner:linked',
  PARTNER_UNLINKED:    'partner:unlinked',
}

const subscribers = new Map()

export function emitDataEvent(eventName, payload) {
  const handlers = subscribers.get(eventName)
  if (!handlers || handlers.size === 0) return
  Array.from(handlers).forEach(handler => {
    try {
      handler(payload)
    } catch (err) {
      console.error(`[dataEvents] handler for "${eventName}" threw:`, err)
    }
  })
}

export function useDataEvent(eventNameOrNames, handler) {
  const handlerRef = useRef(handler)
  useEffect(() => { handlerRef.current = handler }, [handler])

  useEffect(() => {
    const names = Array.isArray(eventNameOrNames)
      ? eventNameOrNames
      : [eventNameOrNames]

    const wrapper = (payload) => handlerRef.current?.(payload)

    names.forEach(name => {
      if (!subscribers.has(name)) subscribers.set(name, new Set())
      subscribers.get(name).add(wrapper)
    })

    return () => {
      names.forEach(name => {
        const set = subscribers.get(name)
        if (set) {
          set.delete(wrapper)
          if (set.size === 0) subscribers.delete(name)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(eventNameOrNames) ? eventNameOrNames.join(',') : eventNameOrNames])
}

export function _debugSubscribers() {
  const out = {}
  subscribers.forEach((set, name) => { out[name] = set.size })
  return out
}